<template>
  <Modal
    class="modal-dark"
    :onClose="onCloseExportModal">
    <template slot="header">
      <h4 class="program-title">
        {{$t('timeShift.export.title')}}
      </h4>
    </template>
    <div class="video-player-container">
      <PreviewByteArkPlayer
        ref="videoPlayer"
        @ready="onPlayerReady"
        :options="playerOptions"
        :statusMessage="statusPreview" />
    </div>
    <div
      v-if="!isSuccess"
      class="modal-container">
      <md-field>
        <label>{{$t('timeShift.export.clipTitle')}}</label>
        <md-input v-model="clipTitle"></md-input>
      </md-field>
      <template v-if="!isSuccess && error">
        <div class="alert alert-success">
          <strong><i class="fas fa-times"></i> {{$t('timeShift.export.error.title')}}</strong><br>
          <span class="inner-text">{{$t('timeShift.export.error.helpText')}} (Error: {{error}})</span>
        </div>
      </template>
    </div>
    <template v-if="isSuccess">
      <div class="alert alert-success">
        <strong><i class="fas fa-check-circle"></i> {{$t('timeShift.export.success.title')}}</strong><br>
        <span class="inner-text">{{$t('timeShift.export.success.helpText')}}</span>
      </div>
    </template>
    <template slot="footer">
      <div
        v-if="!isSuccess"
        class="action-buttons-container">
        <md-button
          @click.stop="onExportClicked"
          :disabled="isSaving"
          class="md-primary md-raised">
          <i class="fas fa-cut"></i> {{$t('timeShift.export.button.save')}}
        </md-button>
      </div>
      <div
        v-if="isSuccess"
        class="action-buttons-container">
        <md-button
          @click.stop="onGoToClipBrowserClicked"
          class="md-primary">
          <i class="fas fa-download"></i> {{$t('timeShift.export.button.goToDownload')}}
        </md-button>
        <md-button
          @click.stop="onCloseExportModal"
          class="md-primary md-raised">
          <i class="fas fa-times"></i> {{$t('timeShift.export.button.dismiss')}}
        </md-button>
      </div>
    </template>
  </Modal>
</template>

<script>
import _ from 'lodash';
import { mapState } from 'vuex';
import PreviewByteArkPlayer from '@/components/elements/PreviewByteArkPlayer.vue';
import Modal from '@/components/elements/Modal.vue';
import { addMilliseconds, parseISO } from 'date-fns';
import { downloadClip } from '@/api/TimeMachine';

export default {
  props: {
    source: {
      type: Object,
      required: true,
    },
  },
  components: {
    Modal,
    PreviewByteArkPlayer,
  },
  data() {
    return {
      mediaURL: null,
      clipTitle: '',
      player: null,
      apiError: false,
      playerOptions: {
        autoplay: true,
        fluid: true,
        aspectRatio: '16:9',
        sources: {
          src: null,
          type: 'application/x-mpegURL',
        },
        transparentContainer: true,
      },
      statusPreview: {
        error: null,
        type: null,
      },
      start: null,
      end: null,
      selectedSource: null,
      isSaving: false,
      isSuccess: false,
      error: null,
    };
  },
  computed: {
    ...mapState({
      program: state => state.timeShift.currentExportProgram,
    }),
    teamName() {
      return this.$route.params.teamName;
    },
  },
  created() {
    this.mediaURL = _.get(this.program, 'mediaUrl', null);
    this.playerOptions.sources.src = this.mediaURL;
    this.getInitialClipTitle();
    this.getShiftedScheduleTime();
  },
  methods: {
    getInitialClipTitle() {
      const programTitle = _.get(this.program, 'title', '');
      const programDate = _.get(this.program, 'scheduleDate', '');
      this.clipTitle = `${programTitle} (${programDate})`;
    },
    getShiftedScheduleTime() {
      const scheduleStart = _.get(this.program, 'schedule.startedAt', '');
      const scheduleEnd = _.get(this.program, 'schedule.endedAt', '');
      let start = _.get(this.program, 'accurateSchedule.startedAt', scheduleStart);
      let end = _.get(this.program, 'accurateSchedule.endedAt', scheduleEnd);
      start = parseISO(start);
      end = parseISO(end);
      const startOffsetMs = _.get(this.program, 'adjustment.startedAtOffsetMs', 0);
      const durationOffsetMs = _.get(this.program, 'adjustment.durationOffsetMs', 0);

      start = addMilliseconds(start, startOffsetMs);
      end = addMilliseconds(end, startOffsetMs);
      end = addMilliseconds(end, durationOffsetMs);
      this.start = new Date(start);
      this.end = new Date(end);
    },
    onCloseExportModal() {
      this.$store.dispatch('resetCurrentExportProgram');
    },
    onGoToClipBrowserClicked() {
      this.$store.dispatch('resetCurrentExportProgram');
      this.$router.push(`/${this.teamName}/clips`);
    },
    onPlayerReady(player) {
      this.player = player;
    },
    async onExportClicked() {
      this.isSaving = true;
      const payload = {
        title: this.clipTitle,
        channelId: this.source._id,
        startedAt: this.start,
        endedAt: this.end,
      };
      try {
        await downloadClip(payload);
        this.isSaving = false;
        this.isSuccess = true;
      } catch (error) {
        this.error = error.response;
        this.isSaving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/variables.scss';

::v-deep .md-tabs-navigation {
  .md-button-content {
    font-size: 1.1em;
  }
  .md-active {
    .md-button-content {
      font-weight: bold;
    }
  }
}

.program-title {
  margin: 0;
}

.inner-text {
  display: block;
  font-size: 0.9rem;
  line-height: 1.4;
}

.video-player-container {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 280px;
  min-height: 280px;
  background: $dark-lavender;
  color: #FFF;
  > div {
    width: 100%;
    height: 100%;
  }
  .error-container {
    background-color: transparent;
    box-shadow: none;
  }
}

.modal-container {
  margin: 20px 25px;
}

.action-buttons-container {
  text-align: right;
  .btn-export-clip {
    margin-right: 4px;
  }
}

.notice-container {
  text-align: center;
  background-color: #3f454a;
  .notice {
    padding: $spacer;
    margin: 0;
    &.future {
      padding: 80px $spacer;
    }
  }
}

// Override Modal configurations
::v-deep .modal-box {
  .close-button {
    top: 1.05em;
  }
  .modal-container-footer {
    margin-top: -8px;
    padding: 0 16px 20px;
  }
}
</style>
